import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, LinearProgress, Paper } from '@mui/material';

import { gql } from '@graphql/__generated__';
import { ListingReferences } from '@modules/_shared/components/listing-marketing/ListingReferences';
import { MarketingForm } from '@modules/_shared/components/listing-marketing/MarketingForm';
import { ListingPortalsList } from '@modules/_shared/components/listing-overview/ListingPortalsButton';
import { MarketingHomogeneousTitle } from '@modules/listings/ListingMarketing';
import { GET_AVAILABLE_PORTALS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

type ListingPortalsSettingsProps = {
  lotId: string;
  readOnly?: boolean;
};

const GET_LISTING_PORTALS_SETTINGS_QUERY = gql(/* GraphQL */ `
  query GetListingPortalsSettings($listingId: uuid!) {
    lots_by_pk(id: $listingId) {
      id
      offer_type
      ...PortalListingButton
      ...MarketingFormData
      broker {
        id
        teams_users {
          team_id
          user_id
        }
      }
    }
  }
`);

const UPDATE_LISTING_REFERENCE = gql(/* GraphQL */ `
  mutation updateListingReference($id: uuid!, $lot_reference: String) {
    update_lots_by_pk(
      pk_columns: { id: $id }
      _set: { lot_reference: $lot_reference }
    ) {
      id
      lot_reference
    }
  }
`);

export const ListingPortalsSettings: React.FC<ListingPortalsSettingsProps> = ({
  lotId,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(
    GET_LISTING_PORTALS_SETTINGS_QUERY,
    {
      variables: { listingId: lotId },
    },
  );

  const listing = data?.lots_by_pk;

  const { data: portalsData, loading: portalsLoading } = useQuery(
    GET_AVAILABLE_PORTALS,
    {
      variables: {
        team_ids: listing?.broker?.teams_users.map(tu => tu.team_id) ?? [],
        lot_id: listing?.id ?? '',
      },
      skip: listing == null,
    },
  );

  const [updateListing] = useMutation(UPDATE_LISTING_REFERENCE);
  const allPortals = portalsData?.portals ?? [];

  return (
    <>
      {loading || portalsLoading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      ) : (
        <>
          {error != null && (
            <Alert severity="error" sx={{ m: 2 }}>
              <pre>{JSON.stringify(error, null, 2)}</pre>
            </Alert>
          )}
          {listing == null ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {t('No listing data found')}
            </Alert>
          ) : (
            <Box>
              <MarketingHomogeneousTitle title={t('Available portals')} />
              <Paper sx={{ px: 2, mb: 2 }}>
                <ListingPortalsList
                  listing={listing}
                  allowSynchronization={!readOnly}
                  fullWidth
                  allPortals={allPortals}
                  readOnly={readOnly}
                />
              </Paper>

              <Box
                sx={{
                  mb: 3,
                }}
              >
                <MarketingHomogeneousTitle title={t('Listing references')} />
                <ListingReferences
                  listingId={listing.id}
                  readOnly={readOnly}
                  onReferenceChanged={reference =>
                    updateListing({
                      variables: {
                        id: listing.id,
                        lot_reference: reference,
                      },
                    })
                  }
                />
              </Box>

              <MarketingForm listing={listing} readOnly={readOnly} />
            </Box>
          )}
        </>
      )}
    </>
  );
};
